import toast from "react-hot-toast";

// Global toast configuration
export const toastConfig = {
  position: "top-right",
  duration: 3000, // Auto-dismiss after 3 seconds
  style: {
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    padding: "12px 16px",
    maxWidth: "400px",
  },
};

// Custom toast with close button
const createToastWithClose = (message, type = "success") => {
  const styles = {
    success: {
      background: "linear-gradient(135deg, #86efac 0%, #22c55e 100%)",
      color: "white",
      border: "1px solid #22c55e",
    },
    error: {
      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      color: "white",
      border: "1px solid #dc2626",
    },
    info: {
      background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      color: "white",
      border: "1px solid #2563eb",
    },
    warning: {
      background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      color: "white",
      border: "1px solid #d97706",
    },
  };

  return toast.custom(
    (t) => (
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          opacity: t.visible ? 1 : 0,
          transition: "all 0.3s ease",
        }}
      >
        <div
          style={{
            ...toastConfig.style,
            ...styles[type],
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            margin: 0,
            borderRadius: "8px 8px 0 0",
            paddingBottom: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
            <span style={{ fontSize: "18px" }}>
              {type === "success" && "✓"}
              {type === "error" && "✕"}
              {type === "info" && "ℹ"}
              {type === "warning" && "⚠"}
            </span>
            <span>{message}</span>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "20px",
              cursor: "pointer",
              padding: "0",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
            }}
          >
            ×
          </button>
        </div>

        {/* Animated Progress Bar */}
        <div
          style={{
            position: "relative",
            height: "4px",
            background: "rgba(255, 255, 255, 0.2)",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              height: "100%",
              background: type === "success"
                ? "linear-gradient(90deg, #86efac, #22c55e, #16a34a)"
                : type === "error"
                ? "linear-gradient(90deg, #fca5a5, #ef4444, #dc2626)"
                : type === "info"
                ? "linear-gradient(90deg, #93c5fd, #3b82f6, #2563eb)"
                : "linear-gradient(90deg, #fcd34d, #f59e0b, #d97706)",
              width: "100%",
              animation: `progressBar ${toastConfig.duration}ms linear forwards`,
              transformOrigin: "right",
            }}
          />
        </div>

        <style>
          {`
            @keyframes progressBar {
              from {
                transform: scaleX(1);
              }
              to {
                transform: scaleX(0);
              }
            }
          `}
        </style>
      </div>
    ),
    {
      duration: toastConfig.duration,
      position: toastConfig.position,
    }
  );
};

// Simplified API
export const showToast = {
  success: (message) => createToastWithClose(message, "success"),
  error: (message) => createToastWithClose(message, "error"),
  info: (message) => createToastWithClose(message, "info"),
  warning: (message) => createToastWithClose(message, "warning"),

  // Loading toast (use with .then() or async/await)
  loading: (message) =>
    toast.loading(message, {
      position: toastConfig.position,
      style: {
        ...toastConfig.style,
        background: "#3b82f6",
        color: "white",
      },
    }),

  // Dismiss a specific toast
  dismiss: (toastId) => toast.dismiss(toastId),

  // Dismiss all toasts
  dismissAll: () => toast.dismiss(),
};

export default showToast;
